/**
 * Harness-level LangGraph interrupt handling.
 *
 * Every interrupt handler in the app used to repeat the same shape: read the
 * interrupt state, parse the payload, dedupe on a canonical serialisation
 * (payloads carry no id), reset when the interrupt clears, and always resume
 * so a failed frontend action can never hang the run. `useInterrupt` owns
 * that shape once; handlers supply a parser and either act on the request
 * automatically (`useInterruptEffect`) or render UI and resume on user action.
 */

import {
  useLangGraphInterruptState,
  useLangGraphSendCommand
} from "@assistant-ui/react-langgraph";
import { useCallback, useEffect, useRef, useState } from "react";

import { stableStringify } from "@/agents-v2/utils/stable-stringify";

/** A parsed, pending interrupt and the function that resumes it. */
export interface PendingInterrupt<T> {
  request: T;
  /** Resume the run with a JSON-serialisable payload. */
  resume: (payload: unknown) => void;
}

/**
 * Track the current interrupt of one type.
 * @param parse - Turns the raw interrupt value into a request, or null when it
 *   is some other interrupt (or none).
 * @returns The pending interrupt, or null. The same interrupt is reported once.
 */
export function useInterrupt<T>(
  parse: (value: unknown) => T | null
): PendingInterrupt<T> | null {
  const interrupt = useLangGraphInterruptState();
  const sendCommand = useLangGraphSendCommand();
  const sendCommandRef = useRef(sendCommand);
  sendCommandRef.current = sendCommand;
  const parseRef = useRef(parse);
  parseRef.current = parse;
  const [pending, setPending] = useState<PendingInterrupt<T> | null>(null);
  const handledKeyRef = useRef<string | null>(null);

  const resume = useCallback((payload: unknown) => {
    setPending(null);
    sendCommandRef.current({ resume: JSON.stringify(payload) });
  }, []);

  useEffect(() => {
    const request = parseRef.current(interrupt?.value);
    if (!request) {
      handledKeyRef.current = null;
      setPending(null);
      return;
    }
    const key = stableStringify(interrupt?.value);
    if (handledKeyRef.current === key) return;
    handledKeyRef.current = key;
    setPending({ request, resume });
  }, [interrupt, resume]);

  return pending;
}

/**
 * Act on an interrupt automatically and resume with the handler's result.
 * A thrown error still resumes, with `fallback()`, so the run never hangs.
 * @param parse - Turns the raw interrupt value into a request, or null.
 * @param handle - Produces the resume payload for a request.
 * @param fallback - The resume payload when `handle` throws.
 */
export function useInterruptEffect<T>(
  parse: (value: unknown) => T | null,
  handle: (request: T) => Promise<unknown> | unknown,
  fallback: (request: T) => unknown
): void {
  const pending = useInterrupt(parse);
  const handleRef = useRef(handle);
  handleRef.current = handle;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  useEffect(() => {
    if (!pending) return;
    const { request, resume } = pending;
    void Promise.resolve()
      .then(() => handleRef.current(request))
      .then(resume)
      .catch((error: unknown) => {
        console.warn("Interrupt handler failed; resuming with fallback", error);
        resume(fallbackRef.current(request));
      });
  }, [pending]);
}
